// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { PERSONA_REGISTRY } from '../constants/persona-registry.js';
import { USE_CASE_PRIMARY_PERSONA } from '../constants/use-case-registry.js';
/**
 * Sprint 62.0.6 — the market-pack persona gating rule.
 *
 * **This is the only implementation.** The persona list, the persona detail
 * endpoint, quick starts, persona detection and the help-centre badge renderer
 * all resolve through it. A second implementation is a review failure (AC4) —
 * writing the rule twice is how the seven `P-VN-` regexes drifted apart.
 *
 * A persona is available when the market pack owning its jurisdiction is
 * installed for the organisation. Callers must pass the **reconciled** install
 * set from `MarketPackService`, never `grc_market_packs.installed`, which that
 * service records at line 41 as possibly stale or missing — a stale flag would
 * hide a persona the organisation is entitled to, and a silent hide is the
 * failure mode nobody reports.
 */
/**
 * Market codes that make `P-EU-` personas relevant.
 *
 * Enumerated rather than derived. The tempting source is `pack.json`'s
 * `region` field, and it is wrong for exactly the two markets that matter here:
 * **UK and NO both declare `"region": "EU"`**. That field means "Europe", not
 * "EU member state", so gating on it would offer EU personas to a UK-only
 * tenant.
 *
 * Includes the EU-27 plus `EU_BASELINE`, plus **`NO`**. Norway is not an EU
 * member state, but it takes on the EU data-protection acquis through the EEA
 * Agreement — the GDPR was incorporated by EEA Joint Committee Decision
 * No 154/2018 and entered into force in Norway, Iceland and Liechtenstein on
 * 20 July 2018 — the `NO` pack ships GDPR, ePrivacy and NIS2 transpositions,
 * and all four `P-EU-` persona documents declare their own region as
 * "European Union / EEA". Excluding it would hide four personas from a
 * Norwegian tenant with no error message.
 *
 * Iceland and Liechtenstein have no market pack yet; when one is added it
 * belongs in this set for the same reason.
 */
export const EU_GATING_MARKET_CODES = [
    // EU-27
    'AT',
    'BE',
    'BG',
    'CY',
    'CZ',
    'DE',
    'DK',
    'EE',
    'ES',
    'FI',
    'FR',
    'GR',
    'HR',
    'HU',
    'IE',
    'IT',
    'LT',
    'LU',
    'LV',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SE',
    'SI',
    'SK',
    // Union-level pack
    'EU_BASELINE',
    // EEA — see note above
    'NO',
];
/**
 * Which installed market codes make each jurisdiction's personas available.
 *
 * `GL` maps to `null`, meaning **never gated**. ISO 27001 and SOC 2 are not
 * tied to a jurisdiction, so a rule of "hide anything not matching my market"
 * would hide precisely the two frameworks that belong to everyone. This is why
 * a tenant with no market pack still gets a defined, non-empty result.
 */
export const JURISDICTION_MARKET_CODES = {
    VN: ['VN'],
    EU: EU_GATING_MARKET_CODES,
    UK: ['UK'],
    GL: null,
};
/** Parse the jurisdiction out of a persona ID (`P-EU-03` → `EU`). */
export function personaJurisdiction(personaId) {
    const match = /^P-([A-Z]{2})-\d+$/.exec(personaId);
    if (!match)
        return null;
    const ns = match[1];
    return ns === 'VN' || ns === 'EU' || ns === 'UK' || ns === 'GL' ? ns : null;
}
/**
 * Is this persona available to an organisation with these installed markets?
 *
 * An unrecognised persona ID is **not** available — an ID we cannot place in a
 * jurisdiction is one we cannot say the organisation is entitled to.
 */
export function isPersonaAvailable(personaId, installedMarketCodes) {
    const jurisdiction = personaJurisdiction(personaId);
    if (!jurisdiction)
        return false;
    const required = JURISDICTION_MARKET_CODES[jurisdiction];
    if (required === null)
        return true; // P-GL- — ungated by design
    const installed = new Set(installedMarketCodes);
    return required.some((code) => installed.has(code));
}
/** Every registered persona ID available to an organisation, in registry order. */
export function availablePersonaIds(installedMarketCodes) {
    const installed = new Set(installedMarketCodes);
    return PERSONA_REGISTRY.filter((p) => isPersonaAvailable(p.persona_id, installed)).map((p) => p.persona_id);
}
/**
 * Every use-case ID whose primary persona is available (AC10).
 *
 * Resolved through the persona rather than by parsing the `UC-` namespace, so
 * there is one rule rather than two that must agree. `persona-registry-drift.test.ts`
 * asserts the two agree anyway — if a use case is ever authored whose primary
 * persona sits in another jurisdiction, that assertion is where it surfaces
 * instead of quietly changing what a tenant can see.
 */
export function availableUseCaseIds(installedMarketCodes) {
    const installed = new Set(installedMarketCodes);
    return Object.entries(USE_CASE_PRIMARY_PERSONA)
        .filter(([, personaId]) => isPersonaAvailable(personaId, installed))
        .map(([useCaseId]) => useCaseId);
}
//# sourceMappingURL=persona-availability.js.map